import type { QueryData } from '#types';
import * as Sentry from '@sentry/nextjs';
import { BuilderError } from '@/types/errors';

type GenericDataProps = {
	id?: string;
	url?: string;
};

type WithData = {
	results?: QueryData<'data' | 'page', GenericDataProps>[];
};

type SortBy = 'createdDate' | 'name';
type SortOrder = 'asc' | 'desc';

export type RestRequestOptions = {
	modelName: string;
	query?: string;
	userAttributes?: string;
	fields?: string;
	sortBy?: SortBy;
	sortOrder?: SortOrder;
	fallback?: string;
};

export const restRequest = async <T>({
	modelName,
	query,
	userAttributes,
	fields,
	sortBy,
	sortOrder = 'desc',
	fallback,
}: RestRequestOptions): Promise<T> => {
	try {
		let queryParams = '';

		if (fallback) {
			const orConditions = [];

			if (query) {
				const eqIndex = query.indexOf('=');
				const field = query.substring(0, eqIndex);
				const value = query.substring(eqIndex + 1);
				orConditions.push({ [field]: { $eq: value } });
			}

			orConditions.push({ id: { $eq: fallback } });

			const jsonQuery = orConditions.length > 1 ? { $or: orConditions } : { id: { $eq: fallback } };

			queryParams = `&options.${modelName}.query=${encodeURIComponent(JSON.stringify(jsonQuery))}`;
		} else if (query) {
			queryParams = `&query.${query}`;
		}

		const userAttributesString = userAttributes ? `&userAttributes.${userAttributes}` : '';
		const fieldsString = fields ? `&fields=${fields}` : '';

		let sortString = '';

		if (sortBy) {
			const direction = sortOrder === 'desc' ? -1 : 1;
			sortString = `&sort.${sortBy}=${direction}`;
		}

		// use preview env var to set if we get only published data or all data.
		const preview = process.env.NEXT_PUBLIC_PREVIEW === 'true';

		// Use cache busting in development OR during build time (CI environment)
		// CI env var is only present during build, not runtime
		const isDevelopment = process.env.NEXT_PUBLIC_ENVIRONMENT === 'development';
		const isBuildTime = process.env.CI === 'true';

		const cacheBust = isDevelopment || isBuildTime ? '&cachebust=true' : '';

		const url =
			'https://cdn.builder.io/api/v3/content/' +
			`${modelName}/` +
			`?apiKey=${process.env.NEXT_PUBLIC_BUILDER_API}` +
			`${queryParams}` +
			`${userAttributesString}` +
			`${fieldsString}` +
			`${sortString}` +
			'&limit=500' +
			`&includeUnpublished=${preview}&enrich=true${cacheBust}`;

		// Use different caching strategies:
		// - Build time (Vercel deployment): no-store for fresh content
		// - Runtime (user requests): force-cache for performance
		const response = await fetch(url, {
			cache: isBuildTime ? 'no-store' : 'force-cache',
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(`HTTP ${response.status}: ${errorText}`);
		}

		const data: WithData = await response.json();

		// when we are in preview mode, we need to fetch all child data, because
		// builder.io does not have a way to fetch all unpublished child data.
		if (preview && data.results?.length) {
			// Use Promise.all for parallel requests instead of sequential
			const previewPromises = data.results.map(async (item) => {
				const previewUrl =
					'https://cdn.builder.io/api/v3/content/' +
					`${modelName}/${item.id}` +
					`?apiKey=${process.env.NEXT_PUBLIC_BUILDER_API}` +
					`&includeUnpublished=${preview}&enrich=true` +
					'&preview=true&noCache=true&cachebust=true';

				const previewResponse = await fetch(previewUrl, {
					cache: isBuildTime ? 'no-store' : 'force-cache',
				});

				if (!previewResponse.ok) {
					const previewErrorText = await previewResponse.text();
					throw new Error(`HTTP ${previewResponse.status}: ${previewErrorText}`);
				}

				return previewResponse.json();
			});

			const results = await Promise.all(previewPromises);
			return results as T;
		}

		return data.results as T;
	} catch (error) {
		// Wrap once so the failure is both reported AND rethrown as a typed BuilderError;
		// callers use `instanceof BuilderError` to know it was already reported here.
		const builderError = new BuilderError(error as Error);

		// Single reporting point: every failure thrown above is reported here exactly
		// once, then rethrown to bubble up to [[...page]].
		Sentry.withScope((scope) => {
			scope.setTag('api', 'builder');
			scope.setLevel('error');

			// Add request context for debugging
			scope.setContext('request_context', {
				modelName,
				query,
				userAttributes,
				fields,
				preview: process.env.NEXT_PUBLIC_PREVIEW === 'true',
				environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
				vercelEnv: process.env.VERCEL_ENV,
			});

			// Classify error types
			if (builderError.message.includes('Rate exceeded') || builderError.message.includes('429')) {
				scope.setTag('error_type', 'rate_limit');
				scope.setLevel('warning');
			} else if (builderError.message.includes('fetch')) {
				scope.setTag('error_type', 'network');
			} else if (builderError.message.includes('JSON')) {
				scope.setTag('error_type', 'json_parse');
			} else {
				scope.setTag('error_type', 'unknown');
			}

			Sentry.captureException(builderError);
		});

		throw builderError;
	}
};
